import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  type ChipProps,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import type { BedrijfDetails, Leesfout, Leesverslag, VacaturepaginaDetails } from '../../api/typen.ts';
import { useVacaturepaginaMutaties, useVacaturepaginas } from '../../api/vacaturepaginas.ts';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { type Meldingsoort, useMeldingen } from '../../componenten/Meldingen.tsx';
import { Tekstveld } from '../../componenten/velden.tsx';
import { datum, datumTijd } from '../../helpers/formatteer.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

const leesfoutLabels: Record<Leesfout, { label: string; uitleg: string }> = {
  onbereikbaar: { label: 'Niet bereikbaar', uitleg: 'De pagina kon bij de laatste poging niet worden opgehaald.' },
  geweigerd: {
    label: 'Lezen niet toegestaan',
    uitleg: 'De website staat het automatisch lezen van deze pagina niet toe.',
  },
  onleesbaar: { label: 'Niet te lezen', uitleg: 'Op de pagina zijn geen vacatures herkend.' },
};

function kortAdres(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
}

function status(pagina: VacaturepaginaDetails): {
  label: string;
  kleur: NonNullable<ChipProps['color']>;
  uitleg: string;
} {
  if (pagina.leesfout) {
    const fout = leesfoutLabels[pagina.leesfout];
    return { label: fout.label, kleur: 'error', uitleg: fout.uitleg };
  }
  if (!pagina.laatstGelezenOp) {
    return { label: 'Nog niet gelezen', kleur: 'default', uitleg: 'Wordt bij de volgende ronde gelezen.' };
  }
  return { label: 'Gelezen', kleur: 'success', uitleg: `Laatst gelezen op ${datumTijd(pagina.laatstGelezenOp)}.` };
}

/**
 * Vat een leesverslag samen in één melding.
 * @param verslag Wat het lezen opleverde.
 * @returns De tekst en de soort melding.
 */
export function leesmelding(verslag: Leesverslag): { tekst: string; soort: Meldingsoort } {
  switch (verslag.uitkomst) {
    case 'ongewijzigd':
      return { tekst: 'De pagina is niet veranderd sinds de vorige keer.', soort: 'info' };
    case 'mislukt':
      return {
        tekst: verslag.leesfout ? leesfoutLabels[verslag.leesfout].uitleg : 'De pagina kon niet worden gelezen.',
        soort: 'error',
      };
    case 'aiNietBeschikbaar':
      return { tekst: 'De AI is even niet beschikbaar. Probeer het later opnieuw.', soort: 'warning' };
    default: {
      const delen = [
        `${verslag.gevonden} ${verslag.gevonden === 1 ? 'vacature' : 'vacatures'} op de pagina`,
        `${verslag.nieuw} nieuw`,
        `${verslag.gekoppeld} gekoppeld`,
        `${verslag.aangevuld} aangevuld`,
      ];
      const rest = verslag.afgekapt || verslag.mislukt > 0 ? ' De rest volgt bij de volgende ronde.' : '';
      return { tekst: `${delen.join(', ')}.${rest}`, soort: 'success' };
    }
  }
}

function PaginaFormulier({ open, onSluit, bedrijfId }: { open: boolean; onSluit: () => void; bedrijfId: string }) {
  const mutaties = useVacaturepaginaMutaties(bedrijfId);
  const formulier = useForm<{ url: string }>({ defaultValues: { url: '' } });
  useEffect(() => {
    if (open) {
      formulier.reset({ url: '' });
    }
  }, [open, formulier]);
  const { onVerzend, fout, bezig } = useVerzenden(formulier, (waarden) => mutaties.voegToe.mutateAsync(waarden), {
    melding: 'Vacaturepagina toegevoegd',
    naSucces: onSluit,
  });
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Vacaturepagina toevoegen"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        verzendLabel="Toevoegen"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Tekstveld
            naam="url"
            label="Adres van de vacaturepagina"
            placeholder="https://www.voorbeeld.nl/werken-bij"
            hulptekst="De pagina waarop het bedrijf zijn vacatures zet, zoals de werken-bij-pagina."
            verplicht
            autoFocus
          />
          <Typography variant="body2" color="text.secondary">
            Het CRM leest de pagina elk uur. Vacatures die al bij dit bedrijf staan, worden aangevuld met wat er op de
            website staat; wat er al is ingevuld, blijft staan. Vacatures die nog ontbreken, worden toegevoegd.
          </Typography>
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

function Vermeldingen({ pagina }: { pagina: VacaturepaginaDetails }) {
  if (pagina.vermeldingen.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {pagina.laatstGelezenOp ? 'Geen vacatures op deze pagina.' : 'Nog geen vacatures gelezen.'}
      </Typography>
    );
  }
  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>Op de website</TableCell>
          <TableCell>In het CRM</TableCell>
          <TableCell>Laatst gezien</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {pagina.vermeldingen.map((vermelding) => {
          const verdwenen = Boolean(pagina.laatstGelezenOp && vermelding.laatstGezienOp < pagina.laatstGelezenOp);
          return (
            <TableRow key={vermelding.id} hover>
              <TableCell>
                <Link href={vermelding.url} target="_blank" rel="noreferrer" underline="hover">
                  {vermelding.titel}
                </Link>
              </TableCell>
              <TableCell>
                {vermelding.vacatureId ? (
                  <Link component={RouterLink} to={`/vacatures/${vermelding.vacatureId}`} underline="hover">
                    Vacature openen
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Verwijderd, wordt niet opnieuw toegevoegd
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {datum(vermelding.laatstGezienOp)}
                {verdwenen && <Chip size="small" color="warning" label="Niet meer op de website" sx={{ ml: 1 }} />}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Pagina({ pagina, bedrijfId }: { pagina: VacaturepaginaDetails; bedrijfId: string }) {
  const mutaties = useVacaturepaginaMutaties(bedrijfId);
  const { meld, meldFout } = useMeldingen();
  const [verwijderen, setVerwijderen] = useState(false);
  const { label, kleur, uitleg } = status(pagina);
  return (
    <Box>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Link href={pagina.url} target="_blank" rel="noreferrer" underline="hover" sx={{ fontWeight: 500 }}>
          {kortAdres(pagina.url)}
          <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
        </Link>
        <Chip size="small" color={kleur} label={label} />
        {!pagina.actief && <Chip size="small" label="Uit" />}
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={pagina.actief}
              disabled={mutaties.wijzig.isPending}
              onChange={(_gebeurtenis, actief) =>
                mutaties.wijzig
                  .mutateAsync({ id: pagina.id, invoer: { actief } })
                  .then(() => meld(actief ? 'Automatisch lezen staat aan' : 'Automatisch lezen staat uit'))
                  .catch(meldFout)
              }
            />
          }
          label="Elk uur lezen"
          slotProps={{ typography: { variant: 'body2' } }}
        />
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          disabled={mutaties.lees.isPending}
          onClick={() =>
            mutaties.lees
              .mutateAsync(pagina.id)
              .then((verslag) => {
                const { tekst, soort } = leesmelding(verslag);
                meld(tekst, soort);
              })
              .catch(meldFout)
          }
        >
          {mutaties.lees.isPending ? 'Bezig met lezen…' : 'Nu lezen'}
        </Button>
        <Tooltip title="Verwijderen">
          <IconButton size="small" aria-label="Vacaturepagina verwijderen" onClick={() => setVerwijderen(true)}>
            <DeleteOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {uitleg}
      </Typography>
      <Vermeldingen pagina={pagina} />
      <BevestigDialoog
        open={verwijderen}
        titel="Vacaturepagina verwijderen"
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={mutaties.verwijder.isPending}
        onSluit={() => setVerwijderen(false)}
        onBevestig={() =>
          mutaties.verwijder
            .mutateAsync(pagina.id)
            .then(() => {
              meld('Vacaturepagina verwijderd');
              setVerwijderen(false);
            })
            .catch(meldFout)
        }
      >
        {kortAdres(pagina.url)} wordt niet meer gelezen. De vacatures die van deze pagina kwamen, blijven staan.
      </BevestigDialoog>
    </Box>
  );
}

/**
 * De vacaturepagina's van een bedrijf: welke pagina's het CRM leest, hoe dat ging en welke vacatures erop staan.
 * @param props Het bedrijf.
 * @returns De kaart.
 */
export function Vacaturepaginas({ bedrijf }: { bedrijf: BedrijfDetails }) {
  const paginas = useVacaturepaginas(bedrijf.id);
  const [toevoegen, setToevoegen] = useState(false);
  return (
    <Card>
      <CardHeader
        title="Vacaturepagina's"
        subheader="Vacatures van de website van het bedrijf, elk uur bijgewerkt"
        action={
          <Button size="small" startIcon={<AddIcon />} onClick={() => setToevoegen(true)}>
            Pagina toevoegen
          </Button>
        }
        slotProps={{ title: { variant: 'h6' }, subheader: { variant: 'body2' } }}
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Laadstatus
          query={paginas}
          leeg="Nog geen vacaturepagina. Voeg de pagina toe waarop dit bedrijf zijn vacatures publiceert; het CRM vult de vacatures van dit bedrijf dan aan met wat op de website staat."
        >
          {(lijst) => (
            <Stack spacing={2} divider={<Divider flexItem />}>
              {lijst.map((pagina) => (
                <Fragment key={pagina.id}>
                  <Pagina pagina={pagina} bedrijfId={bedrijf.id} />
                </Fragment>
              ))}
            </Stack>
          )}
        </Laadstatus>
      </CardContent>
      <PaginaFormulier open={toevoegen} onSluit={() => setToevoegen(false)} bedrijfId={bedrijf.id} />
    </Card>
  );
}
